import Section from '../components/Section';
import { useEffect, useId, useMemo, useState } from 'react';
import ImageUploading from 'react-images-uploading';

import { useAtom } from "jotai";
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { authenticationAtom } from '../State';
import { useVolumes } from '../components/VolumeSelector';
import { isVolumeKeyTaken, nextDefaultVolumeKey, resolveDefaultVolumeKey, restrictedVolumeKeys } from './volumeDefaults';
import { PLATFORM_VOLUME_KEY } from '../StorageAPI';
import { useAdministrationAPI } from '../AdministrationAPI';
import { userAPI } from '../UserAPI';

import SideBar, { ItemType, SideBarItem } from '../components/SideBar';
import { Building, Camera, Copy, Loader2, Lock, Trash2, User } from 'lucide-react';
import { CustomerTheme } from '@the-real-insight/tri-model';
import { EDITABLE_THEME_COLORS, useCustomerTheme } from '../Theme';
import { useModalContext } from '../components/ModalProvider';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/sonner';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

interface Properties {
}

const testSideBarItems: SideBarItem[] = [
    { type: ItemType.item, key: 'test1', text: 'Item 1', icon: <User /> },
    { type: ItemType.item, key: 'test2', text: 'Item 2', icon: <Lock /> },
    { type: ItemType.item, key: 'test3', text: 'Item 3', icon: <Building /> },
];

/** The switches of the company profile; `PUT …/configuration` stores only these (server allow-list). */
type ToolConfiguration = { dontUseWebsearch: boolean; marketplacePublishing: boolean };

const emptyVolumeDraft = {
    key: '',
    name: '',
    graphTenantId: '',
    graphClientId: '',
    graphClientSecret: '',
    sharepointSiteHostname: '',
    sharepointSitePath: '',
    sharepointRootPath: '',
};

/** `rrggbb` oder `#rrggbb` → `#rrggbb`; ungültige Werte werden zu Schwarz. */
const withHash = (value?: string | null): string => {
    const raw = (value ?? '').replace(/^#/, '');
    if (/^[0-9a-fA-F]{3}$/.test(raw)) {
        return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`;
    }
    return /^[0-9a-fA-F]{6}$/.test(raw) ? `#${raw}` : '#000000';
};

export default function CompanyPanel(_properties: Properties) {
    const { t } = useTranslation();
    const { theme, setTheme } = useCustomerTheme();
    const { pushError, showKeywordConfirmDialog } = useModalContext() as any;
    const { createOrUpdateCustomerTheme } = useAdministrationAPI();
    const { updateCustomer } = useAdministrationAPI();
    const { getCustomerToolConfiguration, updateCustomerToolConfiguration } = useAdministrationAPI();
    const { getCustomerRoles } = useAdministrationAPI();
    const [authentication, setAuthentication] = useAtom(authenticationAtom) as any;
    const fieldId = useId();
    const [logoImages, setLogoImages] = useState([]) as any;
    const [customer, setCustomer] = useState({ address: {} }) as any;
    const [loadingCustomer, setLoadingCustomer] = useState(true);
    const [addressDialogOpen, setAddressDialogOpen] = useState(false);
    const [savingAddress, setSavingAddress] = useState(false);
    const [editTheme, setEditTheme] = useState(null) as any;
    const [savingTheme, setSavingTheme] = useState(false);
    const [editField, setEditField] = useState(null) as any;
    const [editColor, setEditColor] = useState(null) as any;
    const [editColorText, setEditColorText] = useState('');
    const [sidebarShown, setSidebarShown] = useState(true);
    const [configuration, setConfiguration] = useState<ToolConfiguration>({ dontUseWebsearch: false, marketplacePublishing: false });
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [deletingLogo, setDeletingLogo] = useState(false);
    const [volumeDialogOpen, setVolumeDialogOpen] = useState(false);
    const [savingVolume, setSavingVolume] = useState(false);
    // The key of the volume being edited, or null when adding a new one. The key itself is
    // immutable once saved — it is baked into every stored ManagedFile.volume.
    const [editingVolumeKey, setEditingVolumeKey] = useState<string | null>(null);
    const [editedVolume, setEditedVolume] = useState<Record<string, string>>({ ...emptyVolumeDraft });

    const dontUseWebsearchId = `${fieldId}-dont-use-websearch`;
    const marketplacePublishingId = `${fieldId}-marketplace-publishing`;
    const defaultVolumeId = `${fieldId}-default-volume`;
    const volumeFieldId = (field: string) => `${fieldId}-volume-${field}`;

    const addressSchema = useMemo(() => z.object({
        street: z.string().trim().min(1, { message: t('settingsPanel.companyPanel.streetNotValid') }),
        streetNumber: z.string().trim().min(1, { message: t('settingsPanel.companyPanel.streetNumberNotValid') }),
        postalCode: z.string().trim().min(1, { message: t('settingsPanel.companyPanel.postalCodeNotValid') }),
        city: z.string().trim().min(1, { message: t('settingsPanel.companyPanel.cityNotValid') }),
    }), [t]);

    type AddressFormValues = z.infer<typeof addressSchema>;

    const addressForm = useForm<AddressFormValues>({
        resolver: zodResolver(addressSchema),
        mode: 'onChange',
        defaultValues: { street: '', streetNumber: '', postalCode: '', city: '' },
    });

    const openAddressDialog = () => {
        addressForm.reset({
            street: customer.address?.street ?? '',
            streetNumber: customer.address?.streetNumber ?? '',
            postalCode: customer.address?.postalCode ?? '',
            city: customer.address?.city ?? '',
        });
        setAddressDialogOpen(true);
    };

    const saveAddress = async (values: AddressFormValues) => {
        setSavingAddress(true);
        try {
            const updatedCustomer = { ...customer, address: { ...customer.address, ...values } };

            await updateCustomer(updatedCustomer);

            setCustomer(updatedCustomer);
            setAddressDialogOpen(false);
            toast.success(t('settingsPanel.companyPanel.addressSaved'));
        } catch (error: any) {
            pushError(error);
        } finally {
            setSavingAddress(false);
        }
    };

    useEffect(() => {
        if (!authentication?.customer) return;
        const call = async () => {
            try {
                const newCustomer = await userAPI.getCustomer(authentication.customer);

                newCustomer.address = newCustomer.address || {};
                // Die Antwort von GET /customers/:id trägt nicht immer ein
                // id-Feld; updateCustomer baut daraus aber die PUT-URL —
                // ohne diesen Rückgriff ging jedes Speichern (Adresse,
                // SharePoint-Ablage) still gegen /customers/undefined.
                newCustomer.id = newCustomer.id ?? authentication.customer;

                setCustomer(newCustomer);
                const config = await getCustomerToolConfiguration(authentication.customer);
                setConfiguration({
                    dontUseWebsearch: config?.dontUseWebsearch === true,
                    marketplacePublishing: config?.marketplacePublishing === true,
                });
            } catch (error: any) {
                pushError(error);
            } finally {
                setLoadingCustomer(false);
            }
        };

        call();
    }, [authentication?.customer, pushError]);

    const saveToolConfiguration = async (change: Partial<ToolConfiguration>) => {
        const previous = configuration;
        const updated = { ...configuration, ...change };
        setConfiguration(updated);
        try {
            await updateCustomerToolConfiguration(authentication.customer, updated);
            // The offer editors read the switch off `customerObject`, which is otherwise only
            // refreshed on the next page load.
            setAuthentication((prev: any) => prev?.customerObject
                ? { ...prev, customerObject: { ...prev.customerObject, configuration: { ...prev.customerObject.configuration, ...change } } }
                : prev);
        } catch (error: any) {
            setConfiguration(previous);
            pushError(error);
        }
    };

    // ---- Additional SharePoint volumes -------------------------------------------------------
    // Stacked on top of the internal S3 volume every customer always has. Adding one is purely
    // additive: nothing moves, and files already stored stay where they are.
    const additionalVolumes: any[] = customer?.configuration?.managedFileVolumes ?? [];

    // The editable list above is the raw configuration — only it carries the Graph credentials, and
    // only entries in it can be edited or removed. Everything about *which volumes exist* comes
    // from the server instead, because the configuration is not the whole truth: a customer who
    // still has the pre-volumes `managedFileVolume` block gets that lifted into a real, browsable
    // volume by `GET /volumes`, and it is invisible here. Reading the configuration directly hid
    // the default-volume dropdown from exactly those customers, who are the ones who most need it.
    const { volumes: serverVolumes, reload: reloadServerVolumes } = useVolumes(authentication?.customer);

    // The customer's roles, for the availability picker on a volume. Offering the real roles is
    // what keeps a typo from silently hiding a volume from everyone — the server cannot validate a
    // role id (its validator is pure) and an unmatched one simply matches nobody.
    const [customerRoles, setCustomerRoles] = useState<Array<{ id: string; name?: string }>>([]);
    useEffect(() => {
        if (!authentication?.customer) return;
        let cancelled = false;
        getCustomerRoles(authentication.customer)
            .then((roles: any) => {
                if (cancelled) return;
                setCustomerRoles(
                    (Array.isArray(roles) ? roles : [])
                        .filter((role: any) => typeof role?.id === 'string' && role.id)
                        .map((role: any) => ({ id: String(role.id), name: role.name }))
                );
            })
            // Not fatal: without the list the dialog just cannot offer a restriction, and every
            // volume stays available to everyone, which is the safe default.
            .catch(() => { if (!cancelled) setCustomerRoles([]); });
        return () => { cancelled = true; };
    }, [authentication?.customer]);

    // Undefined means "never set" and must stay that way — see `persistVolumes`.
    const storedDefaultVolumeKey: string | undefined = customer?.configuration?.defaultManagedFileVolume;
    const defaultVolumeKey = resolveDefaultVolumeKey(serverVolumes, storedDefaultVolumeKey);

    // Held apart from `editedVolume`, which is a flat string map — and `roles` is a list.
    const [editedVolumeRoles, setEditedVolumeRoles] = useState<string[]>([]);

    const openVolumeDialog = (volume?: any) => {
        setEditingVolumeKey(volume?.key ?? null);
        setEditedVolume({ ...emptyVolumeDraft, ...(volume ?? {}), graphClientSecret: '' });
        setEditedVolumeRoles(Array.isArray(volume?.roles) ? volume.roles.map(String) : []);
        setVolumeDialogOpen(true);
    };

    // The key becomes a path segment in every stored `ManagedFile.volume` and in Graph paths, so it
    // is a slug — and it is immutable once saved, because changing it would orphan every file
    // already recorded under the old one.
    const isVolumeKeyValid = (key: string) => /^[a-z0-9][a-z0-9-]{0,63}$/.test(key);

    const trimmedVolumeKey = editedVolume.key?.trim() ?? '';
    const volumeKeyFormatInvalid = trimmedVolumeKey.length > 0
        && (!isVolumeKeyValid(trimmedVolumeKey) || trimmedVolumeKey === 'managed-documents' || trimmedVolumeKey === 'default');
    const volumeKeyTaken = !editingVolumeKey
        && trimmedVolumeKey.length > 0
        && !volumeKeyFormatInvalid
        && isVolumeKeyTaken(trimmedVolumeKey, serverVolumes, additionalVolumes);

    const isVolumeDraftValid = () => {
        const key = editedVolume.key?.trim() ?? '';
        if (!isVolumeKeyValid(key) || key === 'managed-documents' || key === 'default') {
            return false;
        }
        // Taken as `GET /volumes` sees it, which includes a lifted SharePoint volume — the
        // configuration alone does not list one, and claiming its key makes the lift stand down.
        if (!editingVolumeKey && isVolumeKeyTaken(key, serverVolumes, additionalVolumes)) {
            return false;
        }
        const hasRequired = editedVolume.graphTenantId?.trim() &&
            editedVolume.graphClientId?.trim() &&
            editedVolume.sharepointSiteHostname?.trim() &&
            editedVolume.sharepointSitePath?.trim();
        // A brand-new volume needs a secret; an existing one keeps the stored secret when left blank
        // (the client never receives it, so it cannot send it back).
        return !!hasRequired && (editingVolumeKey ? true : !!editedVolume.graphClientSecret?.trim());
    };

    /**
     * `nextDefaultKey`: a key sets the default, `null` clears it, `undefined` leaves it untouched —
     * including leaving it unset.
     *
     * That distinction is the point. Writing the *resolved* default back on every save materialised
     * an explicit `managed-documents` for customers who had never chosen one, which silently
     * repointed where their generated files land: an unmigrated SharePoint customer resolves to
     * their SharePoint site, so saving anything here moved them to an empty S3 volume.
     */
    const persistVolumes = async (volumes: any[], nextDefaultKey?: string | null) => {
        // Not named `configuration` — that is a different piece of state in this component.
        const nextConfiguration: Record<string, any> = {
            ...customer?.configuration,
            managedFileVolumes: volumes,
        };
        const nextDefault = nextDefaultVolumeKey(nextDefaultKey, storedDefaultVolumeKey);
        if (nextDefault) {
            nextConfiguration.defaultManagedFileVolume = nextDefault;
        } else {
            delete nextConfiguration.defaultManagedFileVolume;
        }
        const updatedCustomer = { ...customer, configuration: nextConfiguration };
        await updateCustomer(updatedCustomer);
        setCustomer(updatedCustomer);
        // The volume list the server derives has just changed.
        reloadServerVolumes();
    };

    const saveVolume = async () => {
        setSavingVolume(true);
        try {
            const key = editedVolume.key.trim();
            const draft: Record<string, any> = {
                key,
                name: editedVolume.name?.trim() || key,
                provider: 'microsoftSharePoint',
                graphTenantId: editedVolume.graphTenantId.trim(),
                graphClientId: editedVolume.graphClientId.trim(),
                sharepointSiteHostname: editedVolume.sharepointSiteHostname.trim(),
                sharepointSitePath: editedVolume.sharepointSitePath.trim(),
            };
            if (editedVolume.sharepointRootPath?.trim()) {
                draft.sharepointRootPath = editedVolume.sharepointRootPath.trim();
            }
            // Omitted rather than written as `[]` when nothing is selected: absent is the stored
            // shape that means "available to everyone", and it is what every volume that predates
            // this field already looks like.
            if (editedVolumeRoles.length > 0) {
                draft.roles = editedVolumeRoles;
            }
            const secret = editedVolume.graphClientSecret?.trim();
            if (secret) draft.graphClientSecret = secret;

            const volumes = editingVolumeKey
                ? additionalVolumes.map((volume: any) => (volume.key === editingVolumeKey ? draft : volume))
                : [...additionalVolumes, draft];
            await persistVolumes(volumes);
            setVolumeDialogOpen(false);
            toast.success(t('settingsPanel.companyPanel.fileManagement.volumeSaved'));
        } catch (error: any) {
            pushError(error);
        } finally {
            setSavingVolume(false);
        }
    };

    const removeVolume = (volume: any) => {
        showKeywordConfirmDialog(
            t('settingsPanel.companyPanel.fileManagement.removeVolume', 'Ablage entfernen'),
            t(
                'settingsPanel.companyPanel.fileManagement.removeVolumeWarning',
                'Die Verbindung wird entfernt. Die Dateien in SharePoint bleiben bestehen, sind aber in der Plattform nicht mehr erreichbar.'
            ),
            volume.key,
            t('global.remove', 'Entfernen'),
            async () => {
                try {
                    const volumes = additionalVolumes.filter((candidate: any) => candidate.key !== volume.key);
                    // A default pointing at the volume being removed would be dangling, so it moves
                    // to the platform volume — the one that always exists (CFG-10).
                    //
                    // Named explicitly rather than cleared. Clearing would let the server derive the
                    // default again, which for an unmigrated customer is their lifted SharePoint
                    // volume — arguably a nicer restoration, but it is invisible to whoever just
                    // pressed Remove, and where generated files land should not be inferred. This is
                    // a deliberate, visible action, so it gets a deliberate, visible result.
                    //
                    // Untouched when the default pointed elsewhere: `undefined` leaves it exactly as
                    // it was, including unset. That is the separate fix, and the two must not be
                    // conflated — writing the resolved key back on every save is what silently
                    // repointed customers in the first place.
                    const nextDefault = storedDefaultVolumeKey === volume.key ? PLATFORM_VOLUME_KEY : undefined;
                    await persistVolumes(volumes, nextDefault);
                    toast.success(t('settingsPanel.companyPanel.fileManagement.volumeRemoved'));
                } catch (error: any) {
                    pushError(error);
                }
            }
        );
    };

    const restrictedKeys = restrictedVolumeKeys(additionalVolumes);

    // Built from the server's list, so a lifted SharePoint volume is offered too. The platform
    // volume's stored name is its key, which is not a label — translate that one.
    //
    // A role-restricted volume is left out: the default is where files land for users who named no
    // volume, so the server refuses to let it be one that only some of them can reach. Offering it
    // here would just produce a 400 on save.
    const defaultVolumeOptions = serverVolumes
        .filter(volume => !restrictedKeys.has(volume.key))
        .map(volume => ({
            key: volume.key,
            text: volume.isPlatform && volume.name === volume.key
                ? t('settingsPanel.companyPanel.fileManagement.platformVolume', 'Standard-Ablage')
                : volume.name || volume.key,
        }));

    const openColorDialog = (field: string) => {
        setEditColor(editTheme[field]);
        setEditColorText(withHash(editTheme[field]));
        setEditField(field);
    };

    const saveTheme = async () => {
        setSavingTheme(true);
        try {
            await createOrUpdateCustomerTheme(new CustomerTheme({ ...editTheme }));

            // Update the theme state immediately
            setTheme({ ...editTheme });

            setEditTheme(null);
            toast.success(t('settingsPanel.companyPanel.themeSaved'));
        } catch (error) {
            pushError(error);
        } finally {
            setSavingTheme(false);
        }
    };

    const deleteLogo = async () => {
        setDeletingLogo(true);
        try {
            // Create new theme without logoURL
            const { logoURL: _logoURL, ...themeWithoutLogo } = theme;
            const newCustomerTheme = new CustomerTheme(themeWithoutLogo);

            await createOrUpdateCustomerTheme(newCustomerTheme);

            // Update local theme state
            const { logoURL: _removedLogo, ...newThemeState } = theme;
            setTheme(newThemeState);
            toast.success(t('settingsPanel.companyPanel.logoDeleted'));
        } catch (error) {
            pushError(error);
        } finally {
            setDeletingLogo(false);
        }
    };

    const uploadLogo = async (images: any) => {
        if (!images || images.length === 0) {
            return;
        }

        // Update logoImages state to show the uploaded image
        setLogoImages(images);
        setUploadingLogo(true);

        const formData = new FormData();
        formData.append("file", images[0].file);

        try {
            const result = await userAPI.uploadFile(formData);

            if (!result) {
                throw new Error('Upload failed: No result returned from server');
            }

            // The server may return different response formats, so let's be flexible
            const logoURL = result.path || result.url || result.logoURL || result.fileUrl;

            if (!logoURL) {
                throw new Error('Upload failed: No valid URL path returned from server');
            }

            // Update theme with the uploaded logo URL
            const newCustomerTheme = new CustomerTheme({ ...theme });
            newCustomerTheme.logoURL = logoURL;

            await createOrUpdateCustomerTheme(newCustomerTheme);

            // Update local theme state to show logo immediately
            setTheme({ ...theme, logoURL: logoURL });
            setLogoImages([]);
            toast.success(t('settingsPanel.companyPanel.logoSaved'));
        } catch (error) {
            setLogoImages([]);
            pushError(error);
        } finally {
            setUploadingLogo(false);
        }
    };

    return <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
        <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4">
            <Section title={t('settingsPanel.companyPanel.logo')}>
                <ImageUploading
                    value={logoImages}
                    acceptType={['jpg', 'png', 'gif', 'jpeg']}
                    onChange={uploadLogo}
                    maxNumber={1}
                    maxFileSize={8000000}
                >
                    {({ onImageUpload, isDragging, dragProps }) => (
                        // Single logo area that shows current logo or upload zone
                        <div className="flex min-w-0 flex-col items-center gap-2">
                            <p className="text-base font-medium">{t('settingsPanel.companyPanel.companyLogo')}</p>
                            <div
                                className={cn(
                                    'relative flex h-[200px] w-full max-w-[300px] flex-col items-center justify-center gap-2 rounded-md border border-dashed bg-cover bg-center bg-no-repeat p-2 text-center',
                                    isDragging ? 'border-primary bg-accent' : 'border-input',
                                    !theme.logoURL && !isDragging && 'bg-muted',
                                )}
                                style={theme.logoURL
                                    ? { backgroundImage: `url(${userAPI.getResourceURL(theme.logoURL)})` }
                                    : undefined}
                                {...dragProps}
                            >
                                {!theme.logoURL && (
                                    <>
                                        <Button variant="outline" className="gap-2" onClick={onImageUpload}>
                                            <Camera aria-hidden className="h-4 w-4" />
                                            {isDragging
                                                ? t('settingsPanel.companyPanel.dropLogoHere')
                                                : t('settingsPanel.companyPanel.dropFileOrClickToSelect')}
                                        </Button>
                                        <p className="text-xs text-muted-foreground">
                                            {t('settingsPanel.companyPanel.maxSize')}
                                        </p>
                                    </>
                                )}

                                {theme.logoURL && isDragging && (
                                    <span className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">
                                        {t('settingsPanel.companyPanel.dropNewLogoToReplace')}
                                    </span>
                                )}
                            </div>

                            <div className="flex flex-wrap justify-center gap-2">
                                <Button
                                    variant="outline"
                                    className="gap-2"
                                    disabled={uploadingLogo}
                                    onClick={onImageUpload}
                                >
                                    {uploadingLogo
                                        ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                                        : <Camera aria-hidden className="h-4 w-4" />}
                                    {uploadingLogo
                                        ? t('settingsPanel.companyPanel.uploadingLogo')
                                        : theme.logoURL
                                            ? t('settingsPanel.companyPanel.changeLogo')
                                            : t('settingsPanel.companyPanel.selectLogo')}
                                </Button>

                                {theme.logoURL && (
                                    <Button
                                        variant="outline"
                                        className="gap-2 text-destructive hover:text-destructive"
                                        disabled={deletingLogo}
                                        onClick={deleteLogo}
                                    >
                                        {deletingLogo
                                            ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                                            : <Trash2 aria-hidden className="h-4 w-4" />}
                                        {t('settingsPanel.companyPanel.deleteLogo')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </ImageUploading>
            </Section>
            <Section title={t('settingsPanel.companyPanel.theming')}>
                <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex flex-wrap justify-end">
                        <Button variant="outline" className="max-sm:w-full" onClick={() => setEditTheme({ ...theme })}>
                            {t('global.change')}
                        </Button>
                    </div>
                    <div className="flex min-w-0 flex-wrap gap-4">
                        {EDITABLE_THEME_COLORS.map(({ field, labelKey }) => (
                            <div key={field} className="flex flex-col items-center gap-1">
                                <div
                                    className="h-16 w-16 rounded-md border"
                                    style={{ backgroundColor: theme[field] }}
                                />
                                <div className="text-xs">{t(labelKey)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>
            <Section title={t('settingsPanel.companyPanel.fileManagement.title')}>
                <div className="flex min-w-0 flex-col gap-4">
                    {/*
                      * The exclusive "Speicherort: Intern | SharePoint" switch is gone. Every
                      * customer now always has the internal S3 volume, and SharePoint is only
                      * ever an additional volume — so there is no longer a choice to make here,
                      * and the destructive "you are moving your storage location" flow it
                      * guarded has nothing left to move.
                      */}
                    <p className="text-sm text-muted-foreground">
                        {t('settingsPanel.companyPanel.fileManagement.platformVolumeHint')}
                    </p>

                    {/* Additional SharePoint volumes, stacked on top of the platform volume. */}
                    <div className="flex min-w-0 flex-col gap-2">
                        <p className="text-sm font-semibold">
                            {t('settingsPanel.companyPanel.fileManagement.additionalVolumes', 'Weitere SharePoint-Ablagen')}
                        </p>
                        {additionalVolumes.length === 0 && (
                            <p className="text-sm text-muted-foreground">
                                {t('settingsPanel.companyPanel.fileManagement.noAdditionalVolumes', 'Keine weiteren Ablagen konfiguriert.')}
                            </p>
                        )}
                        {additionalVolumes.map((volume: any) => (
                            <div
                                key={volume.key}
                                data-testid="company-volume-row"
                                className="flex min-w-0 flex-wrap items-center gap-2"
                            >
                                <div className="min-w-[160px] flex-1 text-sm">
                                    <div className="min-w-0 break-words font-medium">{volume.name || volume.key}</div>
                                    <div className="min-w-0 break-words text-muted-foreground">
                                        {volume.sharepointSiteHostname}{volume.sharepointSitePath}
                                    </div>
                                    {/* Which volumes are restricted must be visible without opening
                                        each one — otherwise "why can't Sanja see Nord" has no
                                        answer on this screen. */}
                                    {restrictedKeys.has(String(volume.key)) && (
                                        <div className="min-w-0 break-words text-muted-foreground">
                                            {t(
                                                'settingsPanel.companyPanel.fileManagement.volumeRolesSummary',
                                                'Nur für: {{roles}}',
                                                {
                                                    roles: (volume.roles as string[])
                                                        .map(roleId => customerRoles.find(role => role.id === roleId)?.name || roleId)
                                                        .join(', '),
                                                }
                                            )}
                                        </div>
                                    )}
                                </div>
                                <Button variant="outline" onClick={() => openVolumeDialog(volume)}>
                                    {t('global.change')}
                                </Button>
                                <Button
                                    variant="outline"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => removeVolume(volume)}
                                >
                                    {t('global.remove', 'Entfernen')}
                                </Button>
                            </div>
                        ))}
                        <div className="flex flex-wrap">
                            <Button variant="outline" className="max-sm:w-full" onClick={() => openVolumeDialog()}>
                                {t('settingsPanel.companyPanel.fileManagement.addVolume', 'SharePoint hinzufügen')}
                            </Button>
                        </div>
                    </div>

                    {/* Where newly generated files land. Only shown once there is a choice —
                        judged on the volumes the server reports, not on the configured ones, so
                        a customer whose SharePoint volume is only lifted can still choose. */}
                    {defaultVolumeOptions.length > 1 && (
                        <div className="flex min-w-0 flex-col gap-1.5">
                            <Label htmlFor={defaultVolumeId}>
                                {t('settingsPanel.companyPanel.fileManagement.defaultVolume', 'Standard-Ablage für neue Dateien')}
                            </Label>
                            <Select
                                value={defaultVolumeKey}
                                onValueChange={(value) => {
                                    if (!value || value === defaultVolumeKey) {
                                        return;
                                    }
                                    void persistVolumes(additionalVolumes, value).catch(pushError);
                                }}
                            >
                                <SelectTrigger id={defaultVolumeId} className="min-w-0 sm:w-[280px]">
                                    <SelectValue placeholder={t('global.pleaseSelect')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {defaultVolumeOptions.map((option) => (
                                        <SelectItem key={option.key} value={option.key}>{option.text}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
            </Section>
        </div>
        <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4">
            <Section title={t('settingsPanel.companyPanel.toolSettings')}>
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id={dontUseWebsearchId}
                            checked={configuration.dontUseWebsearch}
                            onCheckedChange={(checked) => saveToolConfiguration({ dontUseWebsearch: checked === true })}
                        />
                        <Label htmlFor={dontUseWebsearchId}>{t('settingsPanel.companyPanel.dontUseWebsearch')}</Label>
                    </div>
                    <div className="flex items-start gap-2">
                        <Checkbox
                            id={marketplacePublishingId}
                            className="mt-0.5"
                            checked={configuration.marketplacePublishing}
                            onCheckedChange={(checked) => saveToolConfiguration({ marketplacePublishing: checked === true })}
                        />
                        <div className="flex flex-col gap-1">
                            <Label htmlFor={marketplacePublishingId}>{t('settingsPanel.companyPanel.marketplacePublishing')}</Label>
                            <p className="text-sm text-muted-foreground">{t('settingsPanel.companyPanel.marketplacePublishingHint')}</p>
                        </div>
                    </div>
                </div>
            </Section>
            <Section title={t('settingsPanel.companyPanel.address')}>
                <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex flex-wrap justify-end">
                        <Button variant="outline" className="max-sm:w-full" onClick={openAddressDialog}>
                            {t('global.change')}
                        </Button>
                    </div>
                    {loadingCustomer
                        ? <div className="flex flex-col gap-2">
                            <Skeleton className="h-4 w-2/3" />
                            <Skeleton className="h-4 w-1/2" />
                            <Skeleton className="h-4 w-1/2" />
                        </div>
                        : <address className="min-w-0 break-words text-sm not-italic">
                            <div>{customer.name}</div>
                            <div>{customer.address.street} {customer.address.streetNumber}</div>
                            <div>{customer.address.postalCode} {customer.address.city}</div>
                        </address>}
                </div>
            </Section>
            <Section title={t('settingsPanel.companyPanel.systemData')}>
                <div className="flex min-w-0 flex-col gap-2">
                    <div className="text-sm font-semibold">{t('settingsPanel.companyPanel.customerNumber')}</div>
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="min-w-0 break-all text-sm">{authentication.customer}</span>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        aria-label={t('global.copy')}
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(authentication.customer);
                                                toast.success(t('global.copied'));
                                            } catch (error) {
                                                console.error('Failed to copy customer ID:', error);
                                            }
                                        }}
                                    >
                                        <Copy aria-hidden className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('global.copy')}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {t('settingsPanel.companyPanel.forReceivingVouchers')}
                    </p>
                </div>
            </Section>
        </div>

        {/* Dialog: Adresse ändern */}
        <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
            <DialogContent
                className="max-w-[calc(100vw-2rem)] sm:max-w-[600px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('settingsPanel.companyPanel.changeAddress')}</DialogTitle>
                </DialogHeader>
                <Form {...addressForm}>
                    <form onSubmit={addressForm.handleSubmit(saveAddress)} className="space-y-4">
                        <FormField
                            control={addressForm.control}
                            name="street"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('settingsPanel.companyPanel.street')}
                                        <span aria-hidden className="ml-0.5 text-destructive">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input autoComplete="address-line1" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={addressForm.control}
                            name="streetNumber"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('settingsPanel.companyPanel.streetNumber')}
                                        <span aria-hidden className="ml-0.5 text-destructive">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={addressForm.control}
                            name="postalCode"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('settingsPanel.companyPanel.postalCode')}
                                        <span aria-hidden className="ml-0.5 text-destructive">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input autoComplete="postal-code" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={addressForm.control}
                            name="city"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('settingsPanel.companyPanel.city')}
                                        <span aria-hidden className="ml-0.5 text-destructive">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input autoComplete="address-level2" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <DialogFooter className="gap-2 pt-4 sm:gap-0">
                            <Button type="button" variant="outline" onClick={() => setAddressDialogOpen(false)}>
                                {t('global.cancel')}
                            </Button>
                            <Button type="submit" disabled={!addressForm.formState.isValid || savingAddress}>
                                {savingAddress && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                                {savingAddress ? t('settingsPanel.companyPanel.saving') : t('global.change')}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>

        {/* Dialog: SharePoint-Ablage anlegen/bearbeiten */}
        <Dialog open={volumeDialogOpen} onOpenChange={setVolumeDialogOpen}>
            <DialogContent
                className="max-h-[85vh] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[560px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>
                        {editingVolumeKey
                            ? t('settingsPanel.companyPanel.fileManagement.editVolume', 'Ablage bearbeiten')
                            : t('settingsPanel.companyPanel.fileManagement.addVolume', 'SharePoint hinzufügen')}
                    </DialogTitle>
                </DialogHeader>
                <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={volumeFieldId('key')}>
                            {t('settingsPanel.companyPanel.fileManagement.volumeKey', 'Kennung')}
                            <span aria-hidden className="ml-0.5 text-destructive">*</span>
                        </Label>
                        <Input
                            id={volumeFieldId('key')}
                            // Immutable after creation: it is part of every stored file's volume reference.
                            disabled={!!editingVolumeKey}
                            value={editedVolume.key}
                            aria-invalid={volumeKeyFormatInvalid || volumeKeyTaken ? true : undefined}
                            aria-describedby={`${volumeFieldId('key')}-hint`}
                            onChange={(event) => setEditedVolume({ ...editedVolume, key: event.target.value })}
                        />
                        <p id={`${volumeFieldId('key')}-hint`} className="text-sm text-muted-foreground">
                            {t(
                                'settingsPanel.companyPanel.fileManagement.volumeKeyHint',
                                'Kleinbuchstaben, Ziffern und Bindestriche. Kann später nicht geändert werden.'
                            )}
                        </p>
                        {volumeKeyFormatInvalid && (
                            <p role="alert" className="text-sm text-destructive">
                                {t('settingsPanel.companyPanel.fileManagement.volumeKeyInvalid')}
                            </p>
                        )}
                        {volumeKeyTaken && (
                            <p role="alert" className="text-sm text-destructive">
                                {t('settingsPanel.companyPanel.fileManagement.volumeKeyTaken')}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={volumeFieldId('name')}>
                            {t('settingsPanel.companyPanel.fileManagement.volumeName', 'Anzeigename')}
                        </Label>
                        <Input
                            id={volumeFieldId('name')}
                            value={editedVolume.name}
                            onChange={(event) => setEditedVolume({ ...editedVolume, name: event.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={volumeFieldId('graphTenantId')}>
                            {t('settingsPanel.companyPanel.fileManagement.graphTenantId')}
                            <span aria-hidden className="ml-0.5 text-destructive">*</span>
                        </Label>
                        <Input
                            id={volumeFieldId('graphTenantId')}
                            value={editedVolume.graphTenantId}
                            onChange={(event) => setEditedVolume({ ...editedVolume, graphTenantId: event.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={volumeFieldId('graphClientId')}>
                            {t('settingsPanel.companyPanel.fileManagement.graphClientId')}
                            <span aria-hidden className="ml-0.5 text-destructive">*</span>
                        </Label>
                        <Input
                            id={volumeFieldId('graphClientId')}
                            value={editedVolume.graphClientId}
                            onChange={(event) => setEditedVolume({ ...editedVolume, graphClientId: event.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={volumeFieldId('graphClientSecret')}>
                            {t('settingsPanel.companyPanel.fileManagement.graphClientSecret')}
                            {!editingVolumeKey && <span aria-hidden className="ml-0.5 text-destructive">*</span>}
                        </Label>
                        <Input
                            id={volumeFieldId('graphClientSecret')}
                            type="password"
                            autoComplete="new-password"
                            placeholder={editingVolumeKey
                                ? t('settingsPanel.companyPanel.fileManagement.graphClientSecretPlaceholder')
                                : undefined}
                            value={editedVolume.graphClientSecret}
                            onChange={(event) => setEditedVolume({ ...editedVolume, graphClientSecret: event.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={volumeFieldId('sharepointSiteHostname')}>
                            {t('settingsPanel.companyPanel.fileManagement.sharepointSiteHostname')}
                            <span aria-hidden className="ml-0.5 text-destructive">*</span>
                        </Label>
                        <Input
                            id={volumeFieldId('sharepointSiteHostname')}
                            value={editedVolume.sharepointSiteHostname}
                            onChange={(event) => setEditedVolume({ ...editedVolume, sharepointSiteHostname: event.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={volumeFieldId('sharepointSitePath')}>
                            {t('settingsPanel.companyPanel.fileManagement.sharepointSitePath')}
                            <span aria-hidden className="ml-0.5 text-destructive">*</span>
                        </Label>
                        <Input
                            id={volumeFieldId('sharepointSitePath')}
                            value={editedVolume.sharepointSitePath}
                            onChange={(event) => setEditedVolume({ ...editedVolume, sharepointSitePath: event.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={volumeFieldId('sharepointRootPath')}>
                            {t('settingsPanel.companyPanel.fileManagement.sharepointRootPath', 'Unterordner (optional)')}
                        </Label>
                        <Input
                            id={volumeFieldId('sharepointRootPath')}
                            value={editedVolume.sharepointRootPath}
                            onChange={(event) => setEditedVolume({ ...editedVolume, sharepointRootPath: event.target.value })}
                        />
                    </div>
                    {customerRoles.length > 0 && (
                        <div className="flex flex-col gap-1.5">
                            <Label>
                                {t('settingsPanel.companyPanel.fileManagement.volumeRoles', 'Verfügbar für Rollen (optional)')}
                            </Label>
                            <div className="flex min-w-0 flex-col gap-2">
                                {customerRoles.map(role => (
                                    <div key={role.id} className="flex items-center gap-2">
                                        <Checkbox
                                            id={volumeFieldId(`role-${role.id}`)}
                                            checked={editedVolumeRoles.includes(role.id)}
                                            onCheckedChange={(checked) =>
                                                setEditedVolumeRoles(current =>
                                                    checked
                                                        ? [...current.filter(id => id !== role.id), role.id]
                                                        : current.filter(id => id !== role.id)
                                                )
                                            }
                                        />
                                        <Label
                                            htmlFor={volumeFieldId(`role-${role.id}`)}
                                            className="font-normal"
                                        >
                                            {role.name || role.id}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                            <p className="text-sm text-muted-foreground">
                                {editedVolumeRoles.length === 0
                                    ? t(
                                        'settingsPanel.companyPanel.fileManagement.volumeRolesNoneHint',
                                        'Ohne Auswahl ist diese Ablage für alle Nutzer verfügbar.'
                                    )
                                    : t(
                                        'settingsPanel.companyPanel.fileManagement.volumeRolesSomeHint',
                                        'Nur Nutzer mit einer dieser Rollen sehen die Ablage. Administratoren sehen sie immer. Eine eingeschränkte Ablage kann nicht die Standard-Ablage sein.'
                                    )}
                            </p>
                        </div>
                    )}
                </div>
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button variant="outline" onClick={() => setVolumeDialogOpen(false)}>
                        {t('global.cancel')}
                    </Button>
                    <Button disabled={!isVolumeDraftValid() || savingVolume} onClick={saveVolume}>
                        {savingVolume && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                        {savingVolume ? t('settingsPanel.companyPanel.saving') : t('global.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {/* Dialog: Design ändern */}
        <Dialog open={editTheme !== null} onOpenChange={(open) => { if (!open) setEditTheme(null); }}>
            <DialogContent
                className="max-h-[85vh] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[900px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('settingsPanel.companyPanel.changeTheme')}</DialogTitle>
                </DialogHeader>
                <div className="flex min-w-0 flex-wrap gap-4">
                    <div className="min-w-[280px] flex-1">
                        <h3 className="text-base font-semibold">{t('settingsPanel.companyPanel.samples')}</h3>
                        <div className="mt-4 flex min-w-0 flex-wrap gap-4">
                            <SideBar items={testSideBarItems} setItem={() => { }} shown={sidebarShown} shownChanged={setSidebarShown}></SideBar>
                            <div className="flex min-w-0 flex-col gap-4">
                                <Button variant="outline">{t('settingsPanel.companyPanel.defaultButton')}</Button>
                                <Button>{t('settingsPanel.companyPanel.primaryButton')}</Button>
                                <Tabs defaultValue="tab1">
                                    <TabsList className="max-w-full overflow-x-auto">
                                        <TabsTrigger value="tab1">{t('settingsPanel.companyPanel.tab1')}</TabsTrigger>
                                        <TabsTrigger value="tab2">{t('settingsPanel.companyPanel.tab2')}</TabsTrigger>
                                        <TabsTrigger value="tab3">{t('settingsPanel.companyPanel.tab3')}</TabsTrigger>
                                    </TabsList>
                                </Tabs>
                                <div className="flex items-center gap-2">
                                    <Switch id={`${fieldId}-sample-toggle`} />
                                    <Label htmlFor={`${fieldId}-sample-toggle`}>
                                        {t('settingsPanel.companyPanel.toggle')}
                                    </Label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="min-w-[280px] flex-1">
                        <h3 className="text-base font-semibold">{t('settingsPanel.companyPanel.themeColors')}</h3>
                        <div className="mt-4 flex min-w-0 flex-wrap gap-4">
                            {EDITABLE_THEME_COLORS.map(({ field, labelKey }) => (
                                <div key={field} className="flex flex-col items-center gap-1">
                                    <button
                                        type="button"
                                        aria-label={`${t('settingsPanel.companyPanel.changeColor')}: ${t(labelKey)}`}
                                        className="h-16 w-16 rounded-md border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                        style={{ backgroundColor: editTheme ? editTheme[field] : undefined }}
                                        onClick={() => openColorDialog(field)}
                                    />
                                    <div className="text-xs">{t(labelKey)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button variant="outline" onClick={() => setEditTheme(null)}>
                        {t('global.cancel')}
                    </Button>
                    <Button disabled={savingTheme} onClick={saveTheme}>
                        {savingTheme && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                        {savingTheme ? t('settingsPanel.companyPanel.saving') : t('global.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {/* Dialog: Farbe ändern */}
        <Dialog open={editField !== null} onOpenChange={(open) => { if (!open) setEditField(null); }}>
            <DialogContent
                className="max-w-[calc(100vw-2rem)] sm:max-w-[420px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('settingsPanel.companyPanel.changeColor')}</DialogTitle>
                </DialogHeader>
                <div className="flex min-w-0 flex-wrap items-end gap-3">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`${fieldId}-color-picker`}>
                            {t('settingsPanel.companyPanel.colorPicker')}
                        </Label>
                        <input
                            id={`${fieldId}-color-picker`}
                            type="color"
                            value={withHash(editColor)}
                            className="h-10 w-16 cursor-pointer rounded-md border border-input bg-background p-1"
                            onChange={(event) => {
                                setEditColor(event.target.value.replace(/^#/, ''));
                                setEditColorText(event.target.value);
                            }}
                        />
                    </div>
                    <div className="flex min-w-[140px] flex-1 flex-col gap-1.5">
                        <Label htmlFor={`${fieldId}-color-hex`}>
                            {t('settingsPanel.companyPanel.colorHex')}
                        </Label>
                        <Input
                            id={`${fieldId}-color-hex`}
                            value={editColorText}
                            spellCheck={false}
                            autoCapitalize="off"
                            autoCorrect="off"
                            className="font-mono"
                            onChange={(event) => {
                                const value = event.target.value;
                                setEditColorText(value);
                                const normalised = value.replace(/^#/, '');
                                if (/^[0-9a-fA-F]{3}$/.test(normalised) || /^[0-9a-fA-F]{6}$/.test(normalised)) {
                                    setEditColor(withHash(normalised).replace(/^#/, ''));
                                }
                            }}
                        />
                    </div>
                    <div
                        aria-hidden
                        className="h-10 w-10 rounded-md border"
                        style={{ backgroundColor: withHash(editColor) }}
                    />
                </div>
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button variant="outline" onClick={() => setEditField(null)}>
                        {t('global.cancel')}
                    </Button>
                    <Button onClick={() => {
                        const newTheme = { ...editTheme };

                        newTheme[editField] = `#${editColor}`;

                        setEditTheme({ ...newTheme });
                        setEditField(null);
                    }}>
                        {t('global.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>;
}
